// Registros de colección sobre la réplica local-first (@heskala/localfirst).
// Puro: forma de las filas, consulta en memoria (búsqueda, filtros por valor,
// orden, página) y el alcance de una colección. Lo usan la tabla, el
// calendario, el kanban y el formulario cuando la colección está «Disponible
// sin conexión» (docs/diseno-libreria-local-first.md §6.4).

import type { ViewRow } from "@heskala/localfirst/core";
import { matchesValueFilters, parseValueFilters } from "@/convex/companies/collections/value_filters";

/** Alcance del sync de una colección (igual que el servidor). */
export const collectionScope = (collectionId: string) => `records:${collectionId}`;

/** Prefijo con que una relación apunta a un registro creado sin conexión. */
export const UUID_REF = "$uuid:";

/** Registro con la forma de `collection_records` que esperan las vistas. */
export interface LocalRecord {
  _id: string;
  _creationTime: number;
  uuid: string;
  collectionId?: string;
  values: Record<string, unknown>;
  internalControl?: Record<string, unknown>;
  parentId?: string;
  searchText?: string;
  _relations?: unknown;
  /** Tiene cambios que el servidor aún no confirmó. */
  _pending: boolean;
  /** Creado sin conexión: todavía no tiene `_id` del servidor. */
  _localOnly: boolean;
}

export function toRecord(row: ViewRow, collectionId?: string): LocalRecord {
  const meta = (row.meta ?? {}) as Record<string, unknown>;
  return {
    _id: row.docId ?? row.id,
    _creationTime: typeof meta._creationTime === "number" ? meta._creationTime : Date.now(),
    uuid: row.id,
    ...(collectionId ? { collectionId } : {}),
    values: row.values,
    ...(meta.internalControl ? { internalControl: meta.internalControl as Record<string, unknown> } : {}),
    ...(typeof meta.parentId === "string" ? { parentId: meta.parentId } : {}),
    ...(typeof meta.searchText === "string" ? { searchText: meta.searchText } : {}),
    ...(meta._relations ? { _relations: meta._relations } : {}),
    _pending: row.pending,
    _localOnly: !row.docId,
  };
}

/** Fila por uuid o por `_id` del servidor (las URLs usan el `_id`). */
export function findRow(rows: readonly ViewRow[], id: string): ViewRow | undefined {
  return rows.find((row) => row.id === id || row.docId === id);
}

export interface LocalRecordsQuery {
  search?: string;
  /** JSON de `ValueFilter[]` (el mismo de la tabla). */
  valueFilters?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  /** 1-indexado. */
  page?: number;
  pageSize?: number;
}

function searchable(record: LocalRecord): string {
  if (record.searchText) return record.searchText;
  return Object.values(record.values)
    .filter((v) => typeof v === "string" || typeof v === "number")
    .join(" ")
    .toLowerCase();
}

function sortValue(record: LocalRecord, key: string): unknown {
  if (key === "_creationTime") return record._creationTime;
  const field = key.startsWith("values.") ? key.slice("values.".length) : key;
  if (field in record.values) return record.values[field];
  const ic = record.internalControl ?? {};
  return ic[field];
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === undefined || a === null || a === "") return 1;
  if (b === undefined || b === null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/** Lo mismo que hace el servidor con una página de la tabla, en memoria. */
export function queryLocalRecords(
  records: readonly LocalRecord[],
  query: LocalRecordsQuery,
): { page: LocalRecord[]; total: number } {
  let out = records.filter((r) => r.internalControl?.state !== "deleted");
  const filters = parseValueFilters(query.valueFilters);
  if (filters.length > 0) out = out.filter((r) => matchesValueFilters(r.values as never, filters));
  const terms = query.search?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? [];
  if (terms.length > 0) out = out.filter((r) => terms.every((t) => searchable(r).includes(t)));
  const key = query.sortBy && query.sortBy !== "" ? query.sortBy : "_creationTime";
  const dir = query.sortBy ? (query.sortDir ?? "asc") : "desc";
  out = [...out].sort((a, b) => {
    const c = compare(sortValue(a, key), sortValue(b, key));
    return dir === "desc" ? -c : c;
  });
  const total = out.length;
  if (query.pageSize) {
    const page = Math.max(1, query.page ?? 1);
    out = out.slice((page - 1) * query.pageSize, page * query.pageSize);
  }
  return { page: out, total };
}
